import {
  AspectRatio,
  Container,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Button,
  Text,
  Stack,
  Center,
  Spinner,
  Box,
} from "@chakra-ui/react";
import React, { useEffect, useState, useRef } from "react";
import { decryptData, downloadImage } from "../utils/utils";
import CustomButton1 from "./CustomButton1";
import { FiDownload } from "react-icons/fi";

const ImageModal = ({
  url = "",
  isOpen,
  onClose,
  useRemoteIfPossible = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [playVid, setPlayVid] = useState(false);
  const [previewKind, setPreviewKind] = useState(null);
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState(null);
  const [usePdfJs, setUsePdfJs] = useState(false);
  const canvasContainerRef = useRef(null);
  const pdfLibLoadedRef = useRef(false);

  // Load PDF.js library
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      !window.pdfjsLib &&
      !pdfLibLoadedRef.current
    ) {
      pdfLibLoadedRef.current = true;
      const script = document.createElement("script");
      script.src =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          console.log("PDF.js library loaded successfully");
        }
      };

      script.onerror = () => {
        console.error("Failed to load PDF.js library");
      };
    }
  }, []);

  // Render PDF using PDF.js
  useEffect(() => {
    if (
      !usePdfJs ||
      !pdfArrayBuffer ||
      !canvasContainerRef.current ||
      !window.pdfjsLib
    )
      return;

    const renderPDF = async () => {
      try {
        console.log("Starting PDF.js rendering...");
        setLoading(true);

        const loadingTask = window.pdfjsLib.getDocument({
          data: pdfArrayBuffer,
        });
        const pdf = await loadingTask.promise;

        console.log(`PDF loaded: ${pdf.numPages} pages`);

        // Clear previous content
        canvasContainerRef.current.innerHTML = "";

        // Render all pages
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);

          // Create canvas for this page
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          // Calculate scale to fit width
          const containerWidth = canvasContainerRef.current.offsetWidth || 800;
          const viewport = page.getViewport({ scale: 1 });
          const scale = (containerWidth * 0.95) / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          canvas.style.display = "block";
          canvas.style.margin = "10px auto";
          canvas.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
          canvas.style.backgroundColor = "white";

          canvasContainerRef.current.appendChild(canvas);

          // Render page
          const renderContext = {
            canvasContext: context,
            viewport: scaledViewport,
          };

          await page.render(renderContext).promise;
          console.log(`Page ${pageNum} rendered`);
        }

        setLoading(false);
        setPreviewError("");
      } catch (error) {
        console.error("Error rendering PDF with PDF.js:", error);
        setLoading(false);
        setPreviewError("Failed to render PDF. Please try downloading.");
      }
    };

    renderPDF();
  }, [usePdfJs, pdfArrayBuffer]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (e) {}
      }
    };
  }, [blobUrl]);

  useEffect(() => {
    if (!isOpen) {
      setPreviewKind(null);
      setPreviewSrc("");
      setPreviewError("");
      setLoading(false);
      setPdfArrayBuffer(null);
      setUsePdfJs(false);
      if (blobUrl) {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch (e) {}
        setBlobUrl(null);
      }
      return;
    }

    if (!url) return;

    let cancelled = false;

    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
    const videoExts = ["mp4", "webm", "ogg"];

    const tryLoadImage = (src) =>
      new Promise((resolve) => {
        try {
          const img = new window.Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = src;
        } catch (e) {
          resolve(false);
        }
      });

    const tryFetchBlob = async (src) => {
      try {
        const res = await fetch(src, { method: "GET", credentials: "include" });
        if (!res.ok) return { ok: false, status: res.status };
        const contentType = (
          res.headers.get("content-type") || ""
        ).toLowerCase();
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        return { ok: true, contentType, objectUrl, blob };
      } catch (e) {
        return { ok: false, error: e };
      }
    };

    const fetchPdfArrayBuffer = async (src) => {
      try {
        console.log("Fetching PDF from:", src);
        const res = await fetch(src, { method: "GET", credentials: "include" });
        if (!res.ok) {
          console.error("PDF fetch failed:", res.status);
          return { ok: false, status: res.status };
        }
        const arrayBuffer = await res.arrayBuffer();
        console.log("PDF fetched successfully, size:", arrayBuffer.byteLength);
        return { ok: true, arrayBuffer };
      } catch (e) {
        console.error("PDF fetch error:", e);
        return { ok: false, error: e };
      }
    };

    const start = async () => {
      setLoading(true);
      setPreviewError("");

      const ext = url.split(".").pop().trim().toLowerCase();

      // Quick path for images
      if (imageExts.includes(ext)) {
        setPreviewKind("image");
        setPreviewSrc(url);
        setLoading(false);
        return;
      }

      // Quick path for videos
      if (videoExts.includes(ext)) {
        setPreviewKind("video");
        setPreviewSrc(url);
        setLoading(false);
        return;
      }

      // Handle PDFs with PDF.js
      if (ext === "pdf") {
        setPreviewKind("pdf");

        // Try to fetch PDF as ArrayBuffer for PDF.js
        const pdfResult = await fetchPdfArrayBuffer(url);

        if (cancelled) return;

        if (pdfResult.ok && pdfResult.arrayBuffer) {
          console.log("Using PDF.js to render PDF");
          setPdfArrayBuffer(pdfResult.arrayBuffer);
          setUsePdfJs(true);
          return;
        } else {
          // Fallback: try iframe method
          console.log("PDF.js fetch failed, trying iframe fallback");
          const fetched = await tryFetchBlob(url);

          if (cancelled) return;

          if (fetched.ok && fetched.objectUrl) {
            setPreviewSrc(fetched.objectUrl);
            setBlobUrl(fetched.objectUrl);
            setUsePdfJs(false);
            setLoading(false);
          } else {
            setPreviewError("Failed to load PDF. Please try downloading.");
            setLoading(false);
          }
        }
        return;
      }

      // Try to load as an image
      const isImage = await tryLoadImage(url);
      if (cancelled) return;
      if (isImage) {
        setPreviewKind("image");
        setPreviewSrc(url);
        setLoading(false);
        return;
      }

      // Try to fetch resource and make an object url
      const fetched = await tryFetchBlob(url);
      if (cancelled) return;

      if (fetched.ok && fetched.objectUrl) {
        if (fetched.contentType.includes("image/")) {
          setPreviewKind("image");
        } else if (fetched.contentType.includes("video/")) {
          setPreviewKind("video");
        } else if (
          fetched.contentType.includes("pdf") ||
          fetched.contentType.includes("application/")
        ) {
          setPreviewKind("pdf");
          // Try PDF.js for PDFs detected by content-type
          const arrayBuffer = await fetched.blob.arrayBuffer();
          setPdfArrayBuffer(arrayBuffer);
          setUsePdfJs(true);
          return;
        } else {
          setPreviewKind("iframe");
        }

        if (useRemoteIfPossible) {
          setPreviewSrc(url);
          setBlobUrl(fetched.objectUrl);

          const fallbackDelay = 2500;
          const timerId = setTimeout(() => {
            if (cancelled) return;
            if (loading) {
              setPreviewSrc(fetched.objectUrl);
              setBlobUrl(fetched.objectUrl);
              setLoading(false);
            }
          }, fallbackDelay);

          setTimeout(() => clearTimeout(timerId), fallbackDelay + 1000);
        } else {
          setPreviewSrc(fetched.objectUrl);
          setBlobUrl(fetched.objectUrl);
        }
        setLoading(false);
        return;
      }

      if (fetched.status === 401) {
        setPreviewError("Unauthorized (401) — server refused the request.");
      } else {
        setPreviewError("Could not fetch or preview this resource in the app.");
      }

      setPreviewKind("iframe");
      setLoading(false);
    };

    start();

    return () => {
      cancelled = true;
    };
  }, [isOpen, url]);

  const urlExt = url?.split(".").pop().trim().toLowerCase() || "";

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={true}>
      <ModalOverlay />
      <ModalContent
        maxW={"container.xl"}
        maxH={"95%"}
        h={"auto"}
        w={"full"}
        padding={0}
        margin={1}
      >
        <ModalCloseButton zIndex={100} bg={"#99999988"} />
        {url?.split(".").pop().trim() == "pdf" || (
          <ModalHeader>
            <Flex justify={"end"} marginRight={10}>
              <CustomButton1
                title={"Download"}
                onClick={() => downloadImage(url)}
                icon={<FiDownload />}
              />
            </Flex>
          </ModalHeader>
        )}
        <ModalBody
          w={"full"}
          h={"full"}
          position={"relative"}
          overflow={"scroll"}
        >
          {previewKind === "video" || urlExt == "mp4" ? (
            <video
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "430px",
                cursor: "pointer",
              }}
              autoPlay={true}
              controls
              muted={false}
            >
              <source src={previewSrc || url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <>
              {previewKind === "pdf" || urlExt == "pdf" ? (
                <Box w="full" minH="90vh" position="relative" bg="gray.100">
                  {loading && (
                    <Center
                      position="absolute"
                      top={0}
                      bottom={0}
                      left={0}
                      right={0}
                      bg="rgba(255, 255, 255, 0.95)"
                      zIndex={20}
                    >
                      <Stack spacing={4} align="center">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                        <Text fontSize="lg" fontWeight={600} color="gray.700">
                          {usePdfJs ? "Rendering PDF..." : "Loading PDF..."}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Please wait
                        </Text>
                      </Stack>
                    </Center>
                  )}

                  {previewError ? (
                    <Center py={8} minH="90vh">
                      <Stack spacing={4} textAlign="center">
                        <Text fontSize="md" fontWeight={600} color="red.600">
                          {previewError}
                        </Text>
                        <Button
                          onClick={() => downloadImage(url)}
                          colorScheme="blue"
                          leftIcon={<FiDownload />}
                          size="lg"
                        >
                          Download PDF
                        </Button>
                      </Stack>
                    </Center>
                  ) : usePdfJs ? (
                    <Box ref={canvasContainerRef} w="full" minH="90vh" py={4} />
                  ) : (
                    <iframe
                      src={previewSrc || url}
                      title="preview-pdf"
                      style={{
                        width: "100%",
                        height: "100%",
                        minHeight: "90vh",
                        border: "none",
                        display: loading ? "none" : "block",
                      }}
                      loading="eager"
                      onLoad={() => {
                        console.log("PDF iframe loaded successfully");
                        setLoading(false);
                      }}
                      onError={() => {
                        console.error("PDF iframe load error");
                        setLoading(false);
                        setPreviewError("Failed to load PDF preview.");
                      }}
                    />
                  )}
                </Box>
              ) : (
                <>
                  {previewError ? (
                    <Center py={6}>
                      <Stack spacing={3} textAlign="center">
                        <Text color="red.600">{previewError}</Text>
                        <Button
                          onClick={() => downloadImage(url)}
                          colorScheme="yellow"
                        >
                          Download
                        </Button>
                      </Stack>
                    </Center>
                  ) : (
                    <Image
                      src={previewSrc || url}
                      w={"full"}
                      h={"full"}
                      objectFit={"contain"}
                      onLoad={() => setLoading(false)}
                      onError={() => setPreviewError("Preview load failed")}
                    />
                  )}
                </>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ImageModal;
